import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, SendHorizonal } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionId = () => {
  let id = localStorage.getItem("hk-session");
  if (!id) {
    id = `hk-${crypto.randomUUID()}`;
    localStorage.setItem("hk-session", id);
  }
  return id;
};

export default function ChatWidget({ t }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", text: t.chat.greeting }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (raw) => {
    const text = (raw ?? input).trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setStreaming(true);
    setMessages((m) => [...m, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        const snapshot = acc;
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", text: snapshot };
          return copy;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          text: "…",
        };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="ai-chat-toggle"
        onClick={() => setOpen(!open)}
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.8, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-forest text-gold shadow-[0_16px_40px_-10px_rgba(13,40,24,0.55)] ring-1 ring-gold/50"
        aria-label="AI Trade Concierge"
      >
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="ai-chat-panel"
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-4 z-50 flex h-[540px] w-[calc(100vw-2rem)] max-w-[400px] flex-col overflow-hidden rounded-sm border border-gold/25 bg-forest shadow-[0_30px_90px_-20px_rgba(9,29,17,0.7)] sm:right-6"
          >
            <div className="flex items-center gap-3 border-b border-cream/10 bg-carddark px-5 py-4">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gold/15 text-gold">
                <MessageCircle className="h-4 w-4" />
              </span>
              <div>
                <p className="font-serif text-base text-cream">{t.chat.title}</p>
                <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-gold/80">
                  {t.chat.subtitle}
                </p>
              </div>
            </div>

            <div
              data-testid="ai-chat-messages"
              className="flex-1 space-y-4 overflow-y-auto px-5 py-5"
            >
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-sm px-4 py-3 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-gold text-forest"
                        : "border border-cream/10 bg-cream/5 text-cream/90"
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
              ))}
              {streaming && messages[messages.length - 1]?.text === "" && (
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold/70">
                  {t.chat.typing}
                </p>
              )}
              <div ref={bottomRef} />
            </div>

            {messages.length <= 1 && (
              <div className="flex flex-wrap gap-2 px-5 pb-3">
                {t.chat.chips.map((c, i) => (
                  <button
                    key={i}
                    data-testid={`ai-chat-chip-${i}`}
                    onClick={() => send(c)}
                    className="rounded-full border border-gold/30 px-3 py-1.5 text-left text-[11px] text-cream/75 transition-colors duration-300 hover:border-gold hover:text-gold"
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="flex items-center gap-2 border-t border-cream/10 bg-carddark p-3"
            >
              <input
                data-testid="ai-chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t.chat.placeholder}
                className="flex-1 rounded-sm border border-cream/10 bg-forest px-4 py-2.5 text-sm text-cream placeholder:text-cream/35 outline-none transition-colors focus:border-gold/60"
              />
              <button
                data-testid="ai-chat-send"
                type="submit"
                disabled={streaming}
                className="flex h-10 w-10 items-center justify-center rounded-sm bg-gold text-forest transition-transform hover:scale-105 disabled:opacity-50"
                aria-label="Send"
              >
                <SendHorizonal className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
